import { APIRequestAuthorization } from '../interceptors/api/request/authorization';
import { APIResponseMetadataErrors } from '../interceptors/api/response/metadata_errors';
import { APIResponseNoContent } from '../interceptors/api/response/no_content';
import { APIRequestDefaults } from '../interceptors/api/request/defaults';

import { AdapterIO, IAdapterIO, IRequestIO, IResponseIO } from './adapter';
import { StorageAdapterIO } from '../adapters/storage';
import { MemoryAdapterIO } from '../adapters/memory';
import { CookieAdapterIO } from '../adapters/cookie';
import { NullAdapterIO } from '../adapters/null';
import { APIAdapterIO } from '../adapters/api';
import { TOptions } from './interceptor';

import f1 from'../../../pip/library/pi-f1/pi-f1.js';

export type t_interceptor = 'request' | 'response';
export type t_adapter = 'cookie' | 'memory' | 'storage' | 'api' | 'null';

type t_adapter_safe = t_adapter | 'fallback';

/**
 * IO supports building input/output adapters in to complex io transformations.
 *
 * There are only three adapters:
 * - api (for `fetch`)
 * - local (for `LocalStorage`)
 * - session (for `SessionStorage`)
 *
 * You can create `null` io adapters, if you need something similar to a pass-thru solution.
 */
export class IO {
  private readonly __noopAdapter = new NullAdapterIO('noop');

  private readonly __adapters: Record<t_adapter_safe, typeof AdapterIO> = {
    api: APIAdapterIO,
    null: NullAdapterIO,
    cookie: CookieAdapterIO,
    memory: MemoryAdapterIO,
    storage: StorageAdapterIO,
    fallback: NullAdapterIO
  } as Record<t_adapter_safe, typeof AdapterIO>;

  readonly fn = f1.freeze({
    api: {
      request: {
        authorization: new APIRequestAuthorization(),
        defaults: new APIRequestDefaults()
      },
      response: {
        metadataErrors: new APIResponseMetadataErrors(),
        noContent: new APIResponseNoContent()
      }
    }
  });

  build(adapter: 'api', id?: string): APIAdapterIO;
  build(adapter: 'null', id?: string): NullAdapterIO;
  build(adapter: 'cookie', id?: string): CookieAdapterIO;
  build(adapter: 'memory', id?: string): MemoryAdapterIO;
  build(adapter: 'storage', id?: string): StorageAdapterIO;
  build(adapter: t_adapter, id?: string): IAdapterIO<IRequestIO, IResponseIO, TOptions> {
    const Adapter = f1.strategy(this.__adapters, adapter);

    AdapterIO.noopAdapter = this.__noopAdapter;
    return new Adapter<IRequestIO, IResponseIO, TOptions>(id);
  }
}
